import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { environment } from "../../environments/environment";
import { ApiService, Settings } from "./api.service";
import { Meta, Title } from "@angular/platform-browser";
import domainData from '../../assets/json/environment.json';
import { DOCUMENT } from '@angular/common';

interface MovingSizes { 1: string; 2: string; 3: string; 4: string }
interface DestinationTypes {
	1: {fullName: string, keyName: string},
	2: {fullName: string, keyName: string},
}
interface ConsentCookie {
	necessary: string,
	preference: string,
	statistics: string,
	advertising: string
}
declare let ga: Function | undefined;
declare let gtag: Function | undefined;
declare let uetq: any | undefined;
declare var dataLayer: any[];

@Injectable( {
	providedIn: 'root'
} )
export class TrackingService {

	private settings !: Settings;
	private ecommerceSend : boolean = false;
	private analyticsDebug : boolean = false;
	private ga4Debug : boolean = false;
	private consentCookieName : string = 'cookieaccepted';
	private consentCookieContent : ConsentCookie | undefined;
	public gtmLoaded = false;

	// Get moving sizes
	public movingSizes : MovingSizes = {
		1: 'Complete household',
		2: 'Part of household',
		3: 'Few pieces of furniture',
		4: 'Some boxes or luggage',
	};
	private destinationTypes : DestinationTypes = {
		1: {
			fullName: 'International',
			keyName: 'int',
		},
		2: {
			fullName: 'National',
			keyName: 'nat',
		},
	}

	private domainData: any;

	constructor(
		private apiService: ApiService,
		private titleService: Title,
		private metaService: Meta,
		@Inject(DOCUMENT) private doc: any,
		@Inject(LOCALE_ID) public locale: string
	) {

		// Get settings
		this.apiService.getAppSettings().subscribe( response => {
			this.settings = response;

			// Check if there's a meta title
			if( this.settings.meta_title ) {

				this.titleService.setTitle( this.settings.meta_title );
			}
			else {

				// Fallback
				this.titleService.setTitle( this.getMetaTitle() );
			}

			if( this.settings.meta_desc ) {

				this.metaService.addTags([
					{name: 'description', 'content': this.settings.meta_desc}
				] );
			}
		} );

		// Set canonical tag to itself without parameters
		this.setCanonical();

		// Get domain specific settings
		let hostname = window.location.hostname;
		let domain = hostname.replace( 'www.', '' );

		if( ! environment.production && ! environment.staging ) {

			domain = 'sea.localhost';
		}

		this.domainData = domainData[domain as keyof typeof domainData];
		var gaUaId = this.domainData?.gtagId;
		var ga4Id = this.domainData?.ga4Id;
		var bingId = this.domainData?.bingId;
		var hotjarId = this.domainData?.hotjarId;
		var gtmId = this.domainData?.gtmId;

		// Read out consent cookie
		if( this.consentCookieExists() ) {

			this.consentCookieContent = this.consentReadCookie() as ConsentCookie;
		}
		else {

			this.consentCookieContent = {
				necessary: 'false',
				preference: 'false',
				statistics: 'false',
				advertising: 'false',
			};
		}

		this.loadGa4( ga4Id );

		if( this.consentCookieExists() ) {

			console.log( this.consentCookieContent );
			console.log( this.domainData );

			// Need Statistics
			if( gaUaId && gaUaId.length > 0 && this.consentCookieContent.statistics ) {

				this.consentLoadScript( "( function( i, s, o, g, r, a, m ) { i['GoogleAnalyticsObject'] = r; i[r] = i[r] || function() { ( i[r].q = i[r].q || [] ).push( arguments ) }, i[r].l = 1 * new Date(); a = s.createElement( o ), m = s.getElementsByTagName( o )[0]; a.async = 1; a.src = g; m.parentNode.insertBefore( a, m ) } )( window, document, 'script', 'https://www.google-analytics.com/analytics" + (this.analyticsDebug ? "_debug" : "") + ".js', 'ga' ); ga( 'create', '" + gaUaId + "', 'auto' ); ga( 'require', 'ec' );" )
			}

			// Need Statistics & Advertising
			// Only on production
			if( hotjarId && hotjarId.length > 0 && this.consentCookieContent.statistics && this.consentCookieContent.advertising && environment.production ) {

				this.consentLoadScript( "(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:" + hotjarId + ",hjsv:5};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'//static.hotjar.com/c/hotjar-','.js?sv=');" );
			}
		}

		// Load GTM
		this.loadGtm( gtmId );

		// Load bing
		this.loadBing( bingId );
	}

	public consentLoadScript( $content: string ) {

		var $script = document.createElement( 'script' );
		$script.innerHTML = $content;
		document.head.append( $script );
	}

	public setStepWithName( stepName:string ) {

		// On test and local only
		var logTracking = true;

		// Prepare the pageview (GTM or GA4)
		var pageViewData = {
			page_title: 'Form-' + stepName,
			page_location: document.location.origin + '/quote-form/' + stepName,
		}

		// Attempt to do this with GTM
		if( this.gtmLoaded ) {

			this.sendGTMEvent( {
				event: 'page_view',
				page_title: pageViewData.page_title,
				page_location: pageViewData.page_location
			} )

			if( logTracking ) {

				console.table( pageViewData )
			}
		}
		// Otherwise directly with GA4
		else if( typeof gtag !== 'undefined' ) {

			gtag( 'event', 'page_view', pageViewData );

			if( logTracking ) {

				console.table( pageViewData )
			}
		}
	}

	public onSubmitForm( token: string, movingSize: number, destinationType: number, paramCa?: string ) {

		// If already done
		if( this.ecommerceSend ) {

			return;
		}

		this.ecommerceSend = true;

		if( this.gtmLoaded ) {

			this.onSubmitGtm();
		}
		else {

			this.onSubmitGa( token, movingSize, destinationType, paramCa );
		}

		this.onSubmitBing();
		this.setStepWithName( 'thank-you' );
	}

	private onSubmitGa( token: string, movingSize: number, destinationType: number, paramCa?: string ) {

		// Build key
		var $priceKey = 'ecommerce_price_int_' + movingSize;

		// Get price from settings
		// By not-individual we take int as a default
		// ecommerce_price_int_{1-4}
		var $price = this.settings[$priceKey as keyof Settings];

		// Product name based on moving size
		var $name = this.movingSizes[movingSize as keyof MovingSizes];

		// Results in complete name of moving size
		var $destinationTypeName = this.destinationTypes[destinationType as keyof DestinationTypes].fullName;

		// Results in the key name {int/nat}
		var $destinationTypeKeyName = this.destinationTypes[destinationType as keyof DestinationTypes].keyName;

		// If this site has individual pricing for int and nat
		if( this.settings.individual_ecommerce_prices ) {

			// Overwrite price key to take dest into account
			$priceKey = 'ecommerce_price_' + $destinationTypeKeyName + '_' + movingSize;

			// Overwrite price with new priceKey
			$price = this.settings[$priceKey as keyof Settings];

			// Add to name
			$name += ' (' + $destinationTypeName + ')';
		}

		// If ecommerce prices based on utm is enabled and if there is a parameter
		if( this.settings.utm_ecommerce && paramCa ) {

			// Check value from CA
			console.log( paramCa );

			// If prices exist for this parameter
			if( this.settings?.utm_prices?.[paramCa] ) {

				console.log( this.settings.utm_prices[paramCa] )
				$priceKey = 'ecommerce_price_' + $destinationTypeKeyName + '_' + movingSize;
				var $newPrice = this.settings.utm_prices[paramCa]?.[$priceKey as keyof Settings];

				// Failsafe
				if( $newPrice > 0 ) {

					$price = $newPrice;
				}
			}
		}

		if( ! environment.production ) {

			console.log( {ecommerce: 'started', price: $price, name: $name} );
		}

		if( typeof ga !== 'undefined' ) {

			// Start ecommerce
			ga( 'require', 'ecommerce' )

			// Add the transaction
			ga( 'ecommerce:addTransaction', {
				'id': token,
				'revenue': $price,
			} );

			// Add the item
			ga( 'ecommerce:addItem', {
				'id': token,
				'name': $name,
				'price': $price,
				'quantity': 1,
			} );

			// Send the data
			ga( 'ecommerce:send' );

			// Send final page view
			ga( 'send', 'pageview', '/quote-form/finished' );
		}

		if( typeof gtag !== 'undefined' ) {

			var purchaseEvent = {
				transaction_id: token,
				currency: 'EUR',
				value: $price,
				items: [
					{
						item_id: token,
						item_name: $name,
						price: $price,
						quantity: 1,
					}
				]
			}

			if( ! environment.production ) {

				console.log( purchaseEvent );
			}

			gtag( 'event', 'purchase', purchaseEvent );
		}
	}

	private onSubmitBing() {

		// Do bing event
		if( this.domainData?.bingId && this.domainData?.bingId.length > 0 && environment.production ) {

			if( typeof uetq !== 'undefined' ) {

				uetq.push('event', 'submit', {"event_category":"leads"});
			}
		}
	}

	private onSubmitGtm() {

		if( typeof dataLayer !== 'undefined' ) {

			dataLayer.push( {
				'event': 'ThankYouPageView',
				'virtualPageURL': '/thank-you'
			} );
		}
	}

	private consentCookieExists() {

		return ( document.cookie.indexOf( this.consentCookieName + '=' ) >= 0 );
	}

	private consentReadCookie() {

		var $ca = document.cookie.split( ';' );
		for( var i = 0; i < $ca.length; i ++ ) {
			var c = $ca[i];
			while( c.charAt( 0 ) == ' ' ) c = c.substring( 1, c.length );
			if( c.indexOf( this.consentCookieName + '=' ) == 0 ) {
				try {
					return JSON.parse( c.substring( ( this.consentCookieName + '=' ).length, c.length ) );
				} catch (e) {
					return false;
				}
			}
		}
		return false;
	}

	private getMetaTitle(): string {

		// Title per language
		if( this.locale == 'en-US' ) {

			return 'Get quotes';
		}
		else if( this.locale == 'en-GB' ) {

		}
		else if( this.locale == 'nl' ) {

			return 'Offertes aanvragen';
		}
		else if( this.locale == 'de' ) {

			return 'Angebot anfragen';
		}
		else if( this.locale == 'it' ) {

			return 'Richiedi Preventivo';
		}
		else if( this.locale == 'es' ) {

			return 'Solicita presupuestos';
		}
		else if( this.locale == 'fr' ) {

			return 'Demande de devis';
		}

		return 'Get quotes';
	}

	private setCanonical() {

		// Set canonical
		const currentUrlWithoutParams = window.location.origin + window.location.pathname;

		let link: HTMLLinkElement = this.doc.createElement('link');
		link.setAttribute('rel', 'canonical');
		this.doc.head.appendChild(link);
		link.setAttribute('href', currentUrlWithoutParams );
	}

	private loadGa4( ga4Id: string ) {

		// GA4
		if( ga4Id && ga4Id.length > 0 ) {

			this.consentLoadScript("window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);}gtag('consent', 'default', { 'ad_storage': " + this.consentCookieExists() + " && " + this.consentCookieContent?.statistics + " ? 'granted' : 'denied', 'ad_user_data': " + this.consentCookieExists() + " && " + this.consentCookieContent?.advertising + " ? 'granted' : 'denied', 'ad_personalization': " + this.consentCookieExists() + " && " + this.consentCookieContent?.advertising + " ? 'granted' : 'denied', 'analytics_storage': " + this.consentCookieExists() + " && " + this.consentCookieContent?.advertising + " ? 'granted' : 'denied' });");

			var tagManager = document.createElement("script");
			tagManager.type = "text/javascript";
			tagManager.setAttribute("async", "true");
			tagManager.setAttribute("src", "https://www.googletagmanager.com/gtag/js?id=" + ga4Id );

			// Checking if there is a first child
			if( document.documentElement.firstChild ) {

				document.documentElement.firstChild.appendChild(tagManager);
			}

			this.consentLoadScript("window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '"+ga4Id+"'"+(this.ga4Debug ? ", { 'debug_mode':true }" : "" ) + ");");
		}
	}

	private loadGtm( gtmId: string ) {

		if( gtmId && gtmId.length > 0 ) {

			// Load outside of consent for now
			this.consentLoadScript("(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','" + gtmId + "');");

			this.gtmLoaded = true;
		}
	}

	private loadBing( bingId: string ) {

		if( bingId && bingId.length > 0 ) {

			this.consentLoadScript(
				"window.uetq = window.uetq || [];" +
				"var bingConsentDataDefault = {" +
				"'ad_storage': " +
				"(" + this.consentCookieExists() + " && " + this.consentCookieContent?.advertising +
				" ? 'granted' : 'denied')" +
				"};" +
				"window.uetq.push('consent', 'default', bingConsentDataDefault);"
			);

			this.consentLoadScript( '(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"' +  bingId + '"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");' );
		}
	}

	public sendGTMEvent( event: any ) {

		console.log( 'sending tracking event' );

		if (typeof dataLayer !== 'undefined') {
			dataLayer.push( event );
		}
	}
}
